// Scoping a recurring-event edit/delete as a function call, mirroring
// ConfirmProvider.tsx exactly (context + a promise) but with two affirmative
// choices instead of one:
//
//     const askScope = useRecurrenceScope();
//     const scope = await askScope({ title: "Delete recurring event", message: … });
//     if (scope == null) return; // cancelled
//     // scope is "this" | "all"
//
// Asked once, up front, when the user clicks Edit/Delete on an item that's
// part of a series — see docs/timezones.md.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button, Modal } from "@/platform/ui/primitives/index.js";

interface RecurrenceScopeOptions {
  title: string;
  message: ReactNode;
}

export type RecurrenceScope = "this" | "all";
type AskScope = (options: RecurrenceScopeOptions) => Promise<RecurrenceScope | null>;

const RecurrenceScopeContext = createContext<AskScope | null>(null);

interface Pending extends RecurrenceScopeOptions {
  resolve: (scope: RecurrenceScope | null) => void;
}

export function RecurrenceScopeProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  const askScope = useCallback<AskScope>(
    (options) =>
      new Promise<RecurrenceScope | null>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  const settle = useCallback((scope: RecurrenceScope | null) => {
    pendingRef.current?.resolve(scope);
    setPending(null);
  }, []);

  const value = useMemo(() => askScope, [askScope]);

  return (
    <RecurrenceScopeContext.Provider value={value}>
      {children}
      <Modal open={pending != null} onClose={() => settle(null)} title={pending?.title ?? ""}>
        <p className="text-[13px] font-semibold text-ink-soft">{pending?.message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => settle(null)}>
            Cancel
          </Button>
          <Button variant="ghost" onClick={() => settle("this")}>
            This event
          </Button>
          <Button variant="primary" onClick={() => settle("all")}>
            All events
          </Button>
        </div>
      </Modal>
    </RecurrenceScopeContext.Provider>
  );
}

/** Ask which occurrences an edit/delete should apply to. Resolves null if cancelled. */
export function useRecurrenceScope(): AskScope {
  const askScope = useContext(RecurrenceScopeContext);
  if (!askScope)
    throw new Error("useRecurrenceScope must be used inside a <RecurrenceScopeProvider>");
  return askScope;
}
